/**
 * Рамка символа на SVG-чертеже: вписывает рисунок в габарит объекта.
 * Ничего не знает о каталоге — просто вложенный svg со своим viewBox.
 *
 * Символ рисуется в своём viewBox (обычно в миллиметрах объекта) и растягивается
 * ровно в width × height без сохранения пропорций — намеренно: габарит на плане
 * должен быть честным, а рисунок обязан его заполнять. Начало координат — в центре
 * объекта, перенос и поворот делает вызывающий.
 *
 * Предмет внутри своего контура залит белым, а не прозрачный: пол и сетка не просвечивают
 * сквозь кровать, а круглый стол — белый круг, а не квадрат с кругом внутри. Символы рисуют
 * контуры с `fill="none"`, и правило ниже заливает замкнутые из них — прямоугольники, круги,
 * овалы, многоугольники и пути, замкнутые `Z`. Открытые линии (штрихи, дуги) не трогает,
 * иначе они превратились бы в белые клинья. Правило лежит прямо в SVG символа, а не в стилях
 * страницы: лист PDF печатается из копии SVG без них.
 */
import type { FC, ReactNode } from "react";

/**
 * Откуда смотрим на объект: сверху на плане, спереди или сбоку на развёртке.
 * Сбоку — так, что перед предмета смотрит вправо.
 */
export type SymbolFacing = "plan" | "front" | "side";

export interface SymbolProps {
	/** Габариты на чертеже, мм */
	width: number;
	height: number;
	color: string;
	/** Вид: на плане объект виден сверху, на развёртке — спереди. По умолчанию — план */
	facing?: SymbolFacing;
}

/** Компонент символа элемента каталога: получает габарит и цвет, рисует себя сам. */
export type SymbolComponent = FC<SymbolProps>;

/**
 * Виды предмета для развёртки: спереди (ширина × высота) и сбоку (глубина × высота,
 * перед вправо). Низ рисунка — пол. Сбоку не нарисован — предмет сбоку просто коробка.
 */
export interface ElevationSymbols {
	front: SymbolComponent;
	side?: SymbolComponent;
}

/** Белая заливка замкнутых контуров символа — см. описание модуля. */
const SYMBOL_FILL_STYLE =
	'.symbol-frame :is(rect, circle, ellipse, polygon)[fill="none"], ' +
	'.symbol-frame path[fill="none"]:is([d$="Z"], [d$="z"]) { fill: #ffffff; }';

export const SymbolFrame = ({
	viewBox,
	width,
	height,
	color,
	children,
}: Omit<SymbolProps, "facing"> & { viewBox: string; children: ReactNode }) => (
	<svg
		x={-width / 2}
		y={-height / 2}
		width={width}
		height={height}
		viewBox={viewBox}
		preserveAspectRatio="none"
		color={color}
		className="symbol-frame"
	>
		<style>{SYMBOL_FILL_STYLE}</style>
		{children}
	</svg>
);
